import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getDrop } from '@/lib/api';
import Navbar from '@/components/Navbar';
import CountdownTimer from '@/components/CountdownTimer';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Flame } from 'lucide-react';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

export default function DropDetailPage() {
  const { id } = useParams();
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const dRes = await getDrop(id);
        setDrop(dRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!drop) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">
          <p className="text-muted-foreground">Drop not found</p>
        </div>
      </div>
    );
  }

  const isLive = drop.status === 'live';

  return (
    <div className="min-h-screen bg-background" data-testid="drop-detail-page">
      <Navbar />
      <div className="grain-overlay" />

      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={drop.image_url} alt={drop.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 md:px-8 pb-8">
          <Link to="/calendar" className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All Drops
          </Link>
          <div className="flex items-center gap-3 mb-2">
            {isLive ? (
              <Badge className="bg-accent text-accent-foreground font-bold text-xs rounded-sm uppercase">
                <Flame className="w-3 h-3 mr-1" /> LIVE
              </Badge>
            ) : (
              <Badge className="bg-primary/20 text-primary font-bold text-xs rounded-sm uppercase border border-primary/30">UPCOMING</Badge>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">{drop.name}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <p className="text-muted-foreground text-lg mb-4 max-w-2xl">{drop.description}</p>
        {!isLive && (
          <div className="mb-8">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Drops in</p>
            <CountdownTimer targetDate={drop.release_date} />
          </div>
        )}

        {/* Collectibles in drop */}
        {drop.collectibles && drop.collectibles.length > 0 && (
          <div>
            <h2 className="font-display text-2xl font-bold mb-6">{drop.collectibles.length} Collectibles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {drop.collectibles.map((c, i) => {
                const totalPrice = c.display_price;
                const priceMessage = c.price_status === 'confirmation_required' ? 'Price confirmation required' : 'Live rate temporarily unavailable';
                return (
                  <motion.div
                    key={c.collectible_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link to={`/collectible/${c.collectible_id}`} data-testid={`drop-collectible-${c.collectible_id}`}>
                      <div className="bg-muted/30 border border-white/5 hover:border-primary/50 transition-all duration-300 group rounded-sm overflow-hidden">
                        <div className="aspect-square overflow-hidden">
                          <img src={c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className={`text-xs rounded-sm ${c.metal_type === 'gold' ? 'border-primary/50 text-primary' : 'border-silver/50 text-silver'}`}>
                              {c.metal_type.toUpperCase()} {c.weight}g
                            </Badge>
                          </div>
                          <h3 className="font-display font-bold mb-2">{c.name}</h3>
                          <div className="flex items-center justify-between">
                            <p className={`${MONEY_CLASSNAME} font-semibold text-primary`}>
                              {formatInr(totalPrice) || priceMessage}
                            </p>
                            <p className="text-xs text-muted-foreground">{c.quantity_remaining} left</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
